/**
 * Form Builder - Admin page for editing EOD form questions
 *
 * Allows admins to:
 * - Edit questions for SDR, Closer, and DM EOD forms
 * - Add/remove/reorder questions
 * - Edit name dropdown options
 * - Changes auto-update Google Sheets headers
 */

import React, { useState, useEffect, useCallback } from "react";

// ============================================================================
// TYPES
// ============================================================================

interface FormOption {
  value: string;
  label: string;
}

type FieldType = "select" | "text" | "number" | "date" | "textarea" | "rating";

interface FormQuestion {
  id: string;
  label: string;
  type: FieldType;
  required: boolean;
  hint?: string;
  placeholder?: string;
  options?: FormOption[];
  section: string;
  sectionTitle?: string;
  min?: number;
  max?: number;
  dbField: string;
  sheetColumn: string;
  parser: "string" | "integer" | "number" | "date";
}

interface FormConfig {
  _id: string;
  formType: "sdr" | "closer" | "dm";
  title: string;
  subtitle: string;
  questions: FormQuestion[];
  nameOptions: FormOption[];
  updatedAt: string;
}

const FORM_TYPES: { id: "sdr" | "closer" | "dm"; label: string; url: string }[] = [
  { id: "sdr", label: "SDR EOD", url: "/sdr-eod" },
  { id: "closer", label: "Closer EOD", url: "/closer-eod" },
  { id: "dm", label: "DM EOD", url: "/dm-eod" },
];

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: "text", label: "Text Input" },
  { value: "number", label: "Number Input" },
  { value: "textarea", label: "Text Area" },
  { value: "select", label: "Dropdown" },
  { value: "rating", label: "Rating (0-10)" },
  { value: "date", label: "Date Picker" },
];

const PARSER_TYPES: { value: string; label: string }[] = [
  { value: "string", label: "Text" },
  { value: "integer", label: "Whole Number" },
  { value: "number", label: "Decimal Number" },
  { value: "date", label: "Date" },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function FormBuilder() {
  const [configs, setConfigs] = useState<FormConfig[]>([]);
  const [activeForm, setActiveForm] = useState<"sdr" | "closer" | "dm">("sdr");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "success" | "error">("idle");
  const [editingQuestion, setEditingQuestion] = useState<string | null>(null);
  const [editingNames, setEditingNames] = useState(false);

  const fetchConfigs = useCallback(async () => {
    try {
      const res = await fetch("/api/eod/forms");
      const data = await res.json();
      if (data.success) {
        setConfigs(data.data);
      }
    } catch (error) {
      console.error("Error fetching form configs:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const activeConfig = configs.find((c) => c.formType === activeForm);

  const saveConfig = async (config: FormConfig) => {
    setSaving(true);
    setSaveStatus("idle");
    try {
      const res = await fetch(`/api/eod/forms/${config.formType}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: config.title,
          subtitle: config.subtitle,
          questions: config.questions,
          nameOptions: config.nameOptions,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setConfigs((prev) =>
          prev.map((c) => (c.formType === config.formType ? { ...c, ...data.data } : c))
        );
        setSaveStatus("success");
        setTimeout(() => setSaveStatus("idle"), 3000);
      } else {
        setSaveStatus("error");
      }
    } catch {
      setSaveStatus("error");
    } finally {
      setSaving(false);
    }
  };

  const updateQuestion = (questionId: string, updates: Partial<FormQuestion>) => {
    if (!activeConfig) return;
    const updatedQuestions = activeConfig.questions.map((q) =>
      q.id === questionId ? { ...q, ...updates } : q
    );
    setConfigs((prev) =>
      prev.map((c) =>
        c.formType === activeForm ? { ...c, questions: updatedQuestions } : c
      )
    );
  };

  const addQuestion = () => {
    if (!activeConfig) return;
    const newId = `custom_${Date.now()}`;
    const newQuestion: FormQuestion = {
      id: newId,
      label: "New Question",
      type: "text",
      required: false,
      section: "custom",
      sectionTitle: "Custom Questions",
      dbField: newId,
      sheetColumn: "New Question",
      parser: "string",
      placeholder: "",
    };
    setConfigs((prev) =>
      prev.map((c) =>
        c.formType === activeForm
          ? { ...c, questions: [...c.questions, newQuestion] }
          : c
      )
    );
    setEditingQuestion(newId);
  };

  const removeQuestion = (questionId: string) => {
    if (!activeConfig) return;
    setConfigs((prev) =>
      prev.map((c) =>
        c.formType === activeForm
          ? { ...c, questions: c.questions.filter((q) => q.id !== questionId) }
          : c
      )
    );
    if (editingQuestion === questionId) setEditingQuestion(null);
  };

  const moveQuestion = (questionId: string, direction: "up" | "down") => {
    if (!activeConfig) return;
    const questions = [...activeConfig.questions];
    const idx = questions.findIndex((q) => q.id === questionId);
    if (idx < 0) return;
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= questions.length) return;
    const temp = questions[idx]!;
    questions[idx] = questions[targetIdx]!;
    questions[targetIdx] = temp;
    setConfigs((prev) =>
      prev.map((c) =>
        c.formType === activeForm ? { ...c, questions } : c
      )
    );
  };

  const updateNameOptions = (nameOptions: FormOption[]) => {
    setConfigs((prev) =>
      prev.map((c) =>
        c.formType === activeForm ? { ...c, nameOptions } : c
      )
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-4 border-[var(--brand-primary)]/20 border-t-[var(--brand-primary)] rounded-full animate-spin" />
      </div>
    );
  }

  if (configs.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-muted-foreground mb-2">No form configurations found.</p>
          <p className="text-sm text-muted-foreground">Form configs will be seeded on next server restart.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between opacity-0 animate-fade-in-up">
        <div>
          <h2 className="text-2xl font-bold mb-1">EOD Form Builder</h2>
          <p className="text-muted-foreground">Edit questions for the EOD report forms</p>
        </div>
        <div className="flex items-center gap-3">
          {saveStatus === "success" && (
            <span className="text-sm text-emerald-500 flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Saved & Sheets updated
            </span>
          )}
          {saveStatus === "error" && (
            <span className="text-sm text-red-500">Failed to save</span>
          )}
          <button
            onClick={() => activeConfig && saveConfig(activeConfig)}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-gradient-end)] text-white font-medium hover:shadow-lg hover:shadow-[var(--brand-primary)]/25 transition-all disabled:opacity-50"
          >
            {saving ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            Save Changes
          </button>
        </div>
      </div>

      {/* Form Type Tabs */}
      <div className="flex gap-2 bg-card rounded-xl p-1.5 shadow-brand opacity-0 animate-fade-in-up stagger-1">
        {FORM_TYPES.map((ft) => (
          <button
            key={ft.id}
            onClick={() => {
              setActiveForm(ft.id);
              setEditingQuestion(null);
              setEditingNames(false);
            }}
            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeForm === ft.id
                ? "bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-gradient-end)] text-white shadow-lg"
                : "text-muted-foreground hover:text-foreground hover:bg-muted"
            }`}
          >
            {ft.label}
            <span className="ml-2 text-xs opacity-70">{ft.url}</span>
          </button>
        ))}
      </div>

      {activeConfig && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Question List */}
          <div className="lg:col-span-2 space-y-4">
            {/* Form Title/Subtitle */}
            <div className="bg-card rounded-2xl p-5 shadow-brand opacity-0 animate-fade-in-up stagger-2">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Form Settings</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Title</label>
                  <input
                    type="text"
                    value={activeConfig.title}
                    onChange={(e) =>
                      setConfigs((prev) =>
                        prev.map((c) =>
                          c.formType === activeForm ? { ...c, title: e.target.value } : c
                        )
                      )
                    }
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary)]/20 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Subtitle</label>
                  <input
                    type="text"
                    value={activeConfig.subtitle}
                    onChange={(e) =>
                      setConfigs((prev) =>
                        prev.map((c) =>
                          c.formType === activeForm ? { ...c, subtitle: e.target.value } : c
                        )
                      )
                    }
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary)]/20 outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Name Options */}
            <div className="bg-card rounded-2xl p-5 shadow-brand">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  Team Members ({activeConfig.nameOptions.length})
                </h3>
                <button
                  onClick={() => setEditingNames(!editingNames)}
                  className="text-xs text-[var(--brand-primary)] hover:underline"
                >
                  {editingNames ? "Done" : "Edit Names"}
                </button>
              </div>
              {editingNames ? (
                <NameOptionsEditor
                  options={activeConfig.nameOptions}
                  onChange={updateNameOptions}
                />
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {activeConfig.nameOptions.map((opt) => (
                    <span
                      key={opt.value}
                      className="px-2.5 py-1 rounded-lg bg-muted text-xs text-foreground"
                    >
                      {opt.label}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Questions */}
            <div className="bg-card rounded-2xl shadow-brand overflow-hidden">
              <div className="p-5 border-b border-border flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">Questions ({activeConfig.questions.length})</h3>
                  <p className="text-sm text-muted-foreground">Click a question to edit it</p>
                </div>
                <button
                  onClick={addQuestion}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] text-sm font-medium hover:bg-[var(--brand-primary)]/20 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Question
                </button>
              </div>

              <div className="divide-y divide-border">
                {activeConfig.questions.map((q, idx) => (
                  <QuestionRow
                    key={q.id}
                    question={q}
                    index={idx}
                    isFirst={idx === 0}
                    isLast={idx === activeConfig.questions.length - 1}
                    isEditing={editingQuestion === q.id}
                    onEdit={() => setEditingQuestion(editingQuestion === q.id ? null : q.id)}
                    onUpdate={(updates) => updateQuestion(q.id, updates)}
                    onRemove={() => removeQuestion(q.id)}
                    onMove={(dir) => moveQuestion(q.id, dir)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right: Preview & Info */}
          <div className="space-y-4">
            {/* Live Preview */}
            <div className="bg-card rounded-2xl p-5 shadow-brand opacity-0 animate-fade-in-up stagger-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Form URL</h3>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 rounded-lg bg-muted text-sm text-foreground font-mono">
                  {FORM_TYPES.find((f) => f.id === activeForm)?.url}
                </code>
                <a
                  href={FORM_TYPES.find((f) => f.id === activeForm)?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Google Sheets Info */}
            <div className="bg-card rounded-2xl p-5 shadow-brand">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Google Sheets</h3>
              <div className="space-y-2 text-sm">
                <p className="text-muted-foreground">
                  When you save changes, the Google Sheets headers are automatically updated to match the new questions.
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="text-foreground text-xs">Connected to EOD Master Sheet</span>
                </div>
              </div>
            </div>

            {/* Sheet Column Preview */}
            <div className="bg-card rounded-2xl p-5 shadow-brand">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Sheet Columns Preview
              </h3>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {activeForm !== "dm" && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="w-5 text-muted-foreground font-mono">1</span>
                    <span className="text-foreground">Timestamp</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs">
                  <span className="w-5 text-muted-foreground font-mono">{activeForm !== "dm" ? "2" : "1"}</span>
                  <span className="text-foreground">Who is filling this form out?</span>
                </div>
                {activeForm === "sdr" && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="w-5 text-muted-foreground font-mono">3</span>
                    <span className="text-foreground">
                      {activeConfig.questions.find((q) => q.id === "dialType")?.sheetColumn || "What did you dial today?"}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs">
                  <span className="w-5 text-muted-foreground font-mono">
                    {activeForm === "sdr" ? "4" : activeForm === "dm" ? "2" : "3"}
                  </span>
                  <span className="text-foreground">What date is this EOD For?</span>
                </div>
                {activeConfig.questions
                  .filter((q) => !(activeForm === "sdr" && q.id === "dialType"))
                  .map((q, i) => {
                    const offset = activeForm === "sdr" ? 5 : activeForm === "dm" ? 3 : 4;
                    return (
                      <div key={q.id} className="flex items-center gap-2 text-xs">
                        <span className="w-5 text-muted-foreground font-mono">{offset + i}</span>
                        <span className="text-foreground truncate" title={q.sheetColumn}>
                          {q.sheetColumn}
                        </span>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// QUESTION ROW COMPONENT
// ============================================================================

function QuestionRow({
  question,
  index,
  isFirst,
  isLast,
  isEditing,
  onEdit,
  onUpdate,
  onRemove,
  onMove,
}: {
  question: FormQuestion;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  isEditing: boolean;
  onEdit: () => void;
  onUpdate: (updates: Partial<FormQuestion>) => void;
  onRemove: () => void;
  onMove: (direction: "up" | "down") => void;
}) {
  const typeLabel = FIELD_TYPES.find((f) => f.value === question.type)?.label || question.type;

  return (
    <div className={`transition-colors ${isEditing ? "bg-[var(--brand-primary)]/5" : "hover:bg-muted/50"}`}>
      {/* Summary Row */}
      <div className="flex items-center gap-3 px-5 py-3 cursor-pointer" onClick={onEdit}>
        <span className="text-xs text-muted-foreground font-mono w-5">{index + 1}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{question.label}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{typeLabel}</span>
            {question.required && (
              <span className="text-xs text-red-500">Required</span>
            )}
            <span className="text-xs text-muted-foreground truncate">{question.section}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onMove("up"); }}
            disabled={isFirst}
            className="p-1 rounded text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onMove("down"); }}
            disabled={isLast}
            className="p-1 rounded text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="p-1 rounded text-muted-foreground hover:text-red-500 transition-colors ml-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Edit Panel */}
      {isEditing && (
        <div className="px-5 pb-4 space-y-3 border-t border-border/50 pt-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Question Label</label>
              <input
                type="text"
                value={question.label}
                onChange={(e) => onUpdate({ label: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:border-[var(--brand-primary)] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Field Type</label>
              <select
                value={question.type}
                onChange={(e) => onUpdate({ type: e.target.value as FieldType })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:border-[var(--brand-primary)] outline-none"
              >
                {FIELD_TYPES.map((ft) => (
                  <option key={ft.value} value={ft.value}>{ft.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Sheet Column Header</label>
              <input
                type="text"
                value={question.sheetColumn}
                onChange={(e) => onUpdate({ sheetColumn: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:border-[var(--brand-primary)] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">DB Field Name</label>
              <input
                type="text"
                value={question.dbField}
                onChange={(e) => onUpdate({ dbField: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:border-[var(--brand-primary)] outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Section</label>
              <input
                type="text"
                value={question.section}
                onChange={(e) => onUpdate({ section: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:border-[var(--brand-primary)] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Section Title</label>
              <input
                type="text"
                value={question.sectionTitle || ""}
                onChange={(e) => onUpdate({ sectionTitle: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:border-[var(--brand-primary)] outline-none"
                placeholder="(first in section only)"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Parser</label>
              <select
                value={question.parser}
                onChange={(e) => onUpdate({ parser: e.target.value as FormQuestion["parser"] })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:border-[var(--brand-primary)] outline-none"
              >
                {PARSER_TYPES.map((pt) => (
                  <option key={pt.value} value={pt.value}>{pt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Hint Text</label>
              <input
                type="text"
                value={question.hint || ""}
                onChange={(e) => onUpdate({ hint: e.target.value || undefined })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:border-[var(--brand-primary)] outline-none"
                placeholder="Optional helper text"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Placeholder</label>
              <input
                type="text"
                value={question.placeholder || ""}
                onChange={(e) => onUpdate({ placeholder: e.target.value || undefined })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:border-[var(--brand-primary)] outline-none"
                placeholder="Input placeholder"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={question.required}
                onChange={(e) => onUpdate({ required: e.target.checked })}
                className="rounded border-border"
              />
              <span className="text-sm text-foreground">Required</span>
            </label>
            {(question.type === "number" || question.type === "rating") && (
              <div className="flex items-center gap-2">
                <label className="text-xs text-muted-foreground">Min:</label>
                <input
                  type="number"
                  value={question.min ?? ""}
                  onChange={(e) => onUpdate({ min: e.target.value ? Number(e.target.value) : undefined })}
                  className="w-16 px-2 py-1 rounded border border-border bg-background text-foreground text-xs outline-none"
                />
                <label className="text-xs text-muted-foreground">Max:</label>
                <input
                  type="number"
                  value={question.max ?? ""}
                  onChange={(e) => onUpdate({ max: e.target.value ? Number(e.target.value) : undefined })}
                  className="w-16 px-2 py-1 rounded border border-border bg-background text-foreground text-xs outline-none"
                />
              </div>
            )}
          </div>

          {/* Options Editor for Select fields */}
          {question.type === "select" && (
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-2">Dropdown Options</label>
              <OptionsEditor
                options={question.options || []}
                onChange={(options) => onUpdate({ options })}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// OPTIONS EDITOR
// ============================================================================

function OptionsEditor({
  options,
  onChange,
}: {
  options: FormOption[];
  onChange: (options: FormOption[]) => void;
}) {
  const addOption = () => {
    onChange([...options, { value: "", label: "" }]);
  };

  const updateOption = (idx: number, field: "value" | "label", val: string) => {
    const updated = options.map((o, i) =>
      i === idx ? { ...o, [field]: val } : o
    );
    onChange(updated);
  };

  const removeOption = (idx: number) => {
    onChange(options.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2">
      {options.map((opt, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <input
            type="text"
            value={opt.value}
            onChange={(e) => updateOption(idx, "value", e.target.value)}
            placeholder="Value"
            className="flex-1 px-2.5 py-1.5 rounded-lg border border-border bg-background text-foreground text-xs focus:border-[var(--brand-primary)] outline-none"
          />
          <input
            type="text"
            value={opt.label}
            onChange={(e) => updateOption(idx, "label", e.target.value)}
            placeholder="Label (shown to user)"
            className="flex-[2] px-2.5 py-1.5 rounded-lg border border-border bg-background text-foreground text-xs focus:border-[var(--brand-primary)] outline-none"
          />
          <button
            onClick={() => removeOption(idx)}
            className="p-1 text-muted-foreground hover:text-red-500 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}
      <button
        onClick={addOption}
        className="text-xs text-[var(--brand-primary)] hover:underline"
      >
        + Add Option
      </button>
    </div>
  );
}

// ============================================================================
// NAME OPTIONS EDITOR
// ============================================================================

function NameOptionsEditor({
  options,
  onChange,
}: {
  options: FormOption[];
  onChange: (options: FormOption[]) => void;
}) {
  const [newName, setNewName] = useState("");

  const addName = () => {
    if (!newName.trim()) return;
    onChange([...options, { value: newName.trim(), label: newName.trim() }]);
    setNewName("");
  };

  const removeName = (idx: number) => {
    onChange(options.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt, idx) => (
          <span
            key={idx}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-muted text-xs text-foreground group"
          >
            {opt.label}
            <button
              onClick={() => removeName(idx)}
              className="text-muted-foreground hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addName()}
          placeholder="Add name..."
          className="flex-1 px-3 py-1.5 rounded-lg border border-border bg-background text-foreground text-sm focus:border-[var(--brand-primary)] outline-none"
        />
        <button
          onClick={addName}
          disabled={!newName.trim()}
          className="px-3 py-1.5 rounded-lg bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] text-sm font-medium hover:bg-[var(--brand-primary)]/20 transition-colors disabled:opacity-50"
        >
          Add
        </button>
      </div>
    </div>
  );
}

export default FormBuilder;
